import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  Package,
  Truck,
  Building2,
  FileText,
  ShoppingCart,
  UserCog,
  Menu,
  LogOut,
  Wifi,
  WifiOff,
  ChevronRight,
  HeartHandshake,
  Shield,
  Settings,
  Globe,
  Archive,
  Table2,
} from 'lucide-react';

// Navigation items with role-based access
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'social_worker', 'warehouse', 'finance', 'super_admin'] },
  { name: 'Municipalidades SaaS', href: '/super-admin', icon: Globe, roles: ['super_admin'] },
  { name: 'Atención Personas', href: '/attention', icon: HeartHandshake, roles: ['admin', 'social_worker', 'super_admin'] },
  { name: 'Beneficiarios', href: '/beneficiaries', icon: Users, roles: ['admin', 'social_worker', 'super_admin'] },
  { name: 'Inventario', href: '/inventory', icon: Package, roles: ['admin', 'warehouse', 'super_admin'] },
  { name: 'Inventario General', href: '/inventory-general', icon: Table2, roles: ['admin', 'warehouse', 'finance', 'super_admin'] },
  { name: 'Entregas', href: '/deliveries', icon: Truck, roles: ['admin', 'social_worker', 'warehouse', 'super_admin'] },
  { name: 'Proveedores', href: '/suppliers', icon: Building2, roles: ['admin', 'finance', 'super_admin'] },
  { name: 'Facturas', href: '/invoices', icon: FileText, roles: ['admin', 'finance', 'super_admin'] },
  { name: 'Órdenes de Compra', href: '/purchase-orders', icon: ShoppingCart, roles: ['admin', 'finance', 'super_admin'] },
  { name: 'Usuarios', href: '/users', icon: UserCog, roles: ['admin', 'super_admin'] },
  { name: 'Roles y Permisos', href: '/roles', icon: Shield, roles: ['admin', 'super_admin'] },
  { name: 'Mantenedor', href: '/mantenedor', icon: Archive, roles: ['admin', 'warehouse', 'super_admin'] },
  { name: 'Configuración', href: '/settings', icon: Settings, roles: ['admin', 'super_admin'] },
];

const Sidebar = ({ mobile = false, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();

  const filteredNavigation = navigation.filter(
    (item) => item.roles.includes(user?.role || 'social_worker')
  );

  return (
    <div className={cn('flex flex-col h-full', mobile ? 'pt-4' : '')}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-6">
        <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center shadow-lg">
          <span className="text-white font-bold text-lg">M</span>
        </div>
        <div>
          <h1 className="font-bold text-lg text-slate-900 font-['Manrope']">MuniSocial</h1>
          <p className="text-xs text-slate-500">Gestión Social</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-2 overflow-y-auto mt-2">
        {filteredNavigation.map((item) => {
          const isActive = location.pathname === item.href || 
            (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
          
          const isPacItem = item.href === '/attention';
          
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={cn(
                'sidebar-link group relative overflow-hidden transition-all duration-300',
                isActive && !isPacItem && 'sidebar-link-active',
                isActive && isPacItem && 'ring-2 ring-emerald-500 ring-offset-2',
                isPacItem && 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md hover:from-emerald-600 hover:to-teal-600 transform hover:-translate-y-0.5'
              )}
              data-testid={`nav-${item.href.replace('/', '')}`}
            >
              <item.icon className={cn(
                'w-5 h-5 flex-shrink-0 z-10',
                isPacItem ? 'text-emerald-50' : (isActive ? 'text-teal-600' : 'text-slate-400 group-hover:text-slate-600')
              )} />
              <span className={cn("font-medium z-10", isPacItem && "text-white font-semibold")}>{item.name}</span>
              {isActive && !isPacItem && (
                <ChevronRight className="w-4 h-4 ml-auto text-teal-500 z-10" />
              )}
              {isPacItem && (
                <ChevronRight className="w-4 h-4 ml-auto text-emerald-100 z-10" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200">
        <p className="text-xs text-slate-400 text-center">
          © 2026 MuniSocial v1.0
        </p>
      </div>
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isConnected } = useWebSocket();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-slate-200 shadow-sm">
          <Sidebar />
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <Sidebar mobile onClose={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-teal-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="font-semibold text-slate-900 font-['Manrope']">MuniSocial</span>
          </div>

          <div className="flex items-center gap-2">
            {isConnected ? (
              <Wifi className="w-4 h-4 text-emerald-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-slate-400" />
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 text-sm font-semibold">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-semibold">{user?.name}</span>
                    <span className="text-xs text-slate-500 font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Desktop Header */}
        <header className="hidden lg:flex sticky top-0 z-30 items-center justify-between px-8 py-4 bg-white/80 backdrop-blur-sm border-b border-slate-200">
          <div></div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4 text-emerald-500" />
                  <span>Conectado</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-slate-400" />
                  <span>Desconectado</span>
                </>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-3 px-3" data-testid="user-menu-desktop">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 text-sm font-semibold">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-semibold">{user?.name}</span>
                    <span className="text-xs text-slate-500 font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-desktop-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
